import React, { useMemo } from "react";
import {
  Dialog as DialogMUI,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Breakpoint,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react-lite";

import {
  TranslationContext,
  translationService,
} from "../../services/translation/TranslationService";
import { Icon } from "../../icons/Icon";
import Button from "../button/Button";
import useThemeService, { MUIComponentsTheme } from "../../services/theme-service/ThemeService";
import "./composition.scss";
import Svg from "../svg/Svg";

interface DialogButton {
  id?: string;
  textKey: string;
  action: () => void;
}

export interface DialogOkButton extends DialogButton {
  disabled?: boolean;
}

interface DialogProps {
  isVisible: boolean;
  onHide: () => void;
  iconTitle?: Icon;
  titleKey: string;
  titleContext?: TranslationContext;
  subTextKey?: any;
  subtitleContext?: TranslationContext;
  children?: any;
  buttonOk?: DialogOkButton;
  additionalButton?: DialogOkButton;
  buttonCancel?: DialogButton;
  actionExtras?: React.ReactNode;
  sidePosition?: boolean;
  className?: string;
  maxWidth?: Breakpoint | false;
  paperSx?: SxProps<Theme>;
}

export const Dialog = observer(function DialogComponentMUI(props: DialogProps) {
  const {
    isVisible,
    onHide,
    iconTitle,
    titleKey,
    titleContext,
    subTextKey,
    subtitleContext,
    children,
    buttonOk,
    additionalButton,
    buttonCancel,
    actionExtras,
    sidePosition = false,
    className,
    maxWidth,
    paperSx
  } = {
    ...props,
  };
  const { themeClass } = useThemeService();

  const translatedTitle = translationService.translate(titleKey, titleContext);
  const translatedSubtitle = translationService.translate(subTextKey, subtitleContext);

  const handleClose = (_event: {}, reason: string) => {
    if (reason && reason === "backdropClick") return;
    onHide();
  };

  const cssClasses = useMemo(() => {
    let classes = "dialog";
    if (className) classes += ` ${className}`;
    if (sidePosition) classes += " side-position";

    return classes;
  }, [className, sidePosition]);

  return (
    <ThemeProvider theme={MUIComponentsTheme}>
      <DialogMUI open={isVisible} onClose={handleClose} className={cssClasses} maxWidth={maxWidth} PaperProps={paperSx ? { sx: paperSx } : undefined}>
        <DialogTitle>
          {iconTitle && (
            <div className={`dialog-title no-border ${iconTitle.className}`}>
              <Svg
                svg={iconTitle.svg}
                width={iconTitle.width || 50}
                height={iconTitle.height || 50}
                className={iconTitle.className}
              />
              <span>{translatedTitle}</span>
            </div>
          )}
          {!iconTitle && <div className="dialog-title">{translatedTitle}</div>}
          <IconButton
            aria-label="close"
            onClick={onHide}
            sx={{
              position: "absolute",
              right: 12,
              top: 6,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="content-container">
          {translatedSubtitle && <div className="dialog-subtitle">{translatedSubtitle}</div>}
          {children}
        </DialogContent>
        <DialogActions className="dialog-actions">
          <div className="dialog-actions-container">
            {buttonOk && (
              <Button
                id={buttonOk.id}
                className={themeClass(["btn-big"])}
                textKey={buttonOk.textKey}
                onClick={buttonOk.action}
                disabled={buttonOk.disabled || undefined}
              />
            )}
            {additionalButton && (
              <Button
                id={additionalButton.id}
                className={themeClass(["btn-big"])}
                textKey={additionalButton.textKey}
                onClick={additionalButton.action}
                disabled={additionalButton.disabled || undefined}
              />
            )}
            {buttonCancel && (
              <Button
                id={buttonCancel.id}
                className={themeClass(["btn-big", "fill-color-ghost"])}
                textKey={buttonCancel.textKey}
                onClick={buttonCancel.action}
              />
            )}
            {actionExtras && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                {actionExtras}
              </div>
            )}
          </div>
        </DialogActions>
      </DialogMUI>
    </ThemeProvider>
  );
});
